// modifyInsertValuesCell — VQE-Edit edit-action: byte-precise replace of a
// single `INSERT … VALUES` cell via SR-Edit's `modify-insert-values-cell` op.
// Cell ranges aren't stored on `QueryStructure` — SR-Edit re-splits them from
// the row range at plan time — so this module only validates the row index
// against an optional structure (the cell index is left to the applicator's
// own bounds check, which plans nothing when it's out of range) and delegates
// to `commitEdit`.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — DML edits.

import type { QueryStructure } from '../../../../sql-resolver/types/queryStructure';
import { commitEdit, type CommitEditCtx, type CommitOutcome } from '../commitEdit';

export type ModifyInsertValuesCellOptions = {
  /** Optional structure used for row-index-bounds validation. */
  readonly structure?: QueryStructure;
};

export async function modifyInsertValuesCell(
  rowIndex: number,
  cellIndex: number,
  newText: string,
  ctx: CommitEditCtx,
  options: ModifyInsertValuesCellOptions = {},
): Promise<CommitOutcome> {
  if (!Number.isInteger(rowIndex) || rowIndex < 0) {
    throw new Error(
      `modifyInsertValuesCell: rowIndex must be a non-negative integer (got ${rowIndex})`,
    );
  }
  if (!Number.isInteger(cellIndex) || cellIndex < 0) {
    throw new Error(
      `modifyInsertValuesCell: cellIndex must be a non-negative integer (got ${cellIndex})`,
    );
  }
  const trimmed = newText.trim();
  if (trimmed.length === 0) {
    throw new Error('modifyInsertValuesCell: newText must not be empty');
  }
  if (options.structure) {
    const rows = options.structure.insertValueRowRanges ?? [];
    if (rows.length === 0) {
      throw new Error('modifyInsertValuesCell: VALUES list is empty');
    }
    if (rowIndex >= rows.length) {
      throw new Error(
        `modifyInsertValuesCell: rowIndex ${rowIndex} out of range (VALUES list has ${rows.length} row(s))`,
      );
    }
  }
  return commitEdit(
    { op: 'modify-insert-values-cell', rowIndex, cellIndex, newText: trimmed },
    ctx,
  );
}
